
import React, { createContext, ReactNode, useContext, useState, useEffect, useCallback } from 'react';
import { AuthService } from '@/services/AuthService';
import { queryClient } from '@/utils/api-client';

// Define user type
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  permissions?: string[];
}

// Define auth context type
interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  isAuthenticated: boolean;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  error: null,
  login: async () => ({ success: false }),
  logout: async () => {},
  getToken: async () => null,
  isAuthenticated: false,
});

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth Provider component
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initial auth check only runs once
  useEffect(() => {
    // Skip if already initialized
    if (isInitialized) return;
    
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        
        // Try to get token first
        const token = AuthService.getToken();
        
        // Check if token exists and is valid
        if (token && !AuthService.isTokenExpired()) {
          // Token exists and is valid, try to get user from localStorage first
          const storedUser = AuthService.getCurrentUser();
          
          if (storedUser) {
            setUser(storedUser as User);
          }
          
          // Always validate with API to ensure we have current user data
          try {
            const apiUser = await AuthService.getUserInfoFromApi();
            if (apiUser) {
              setUser(apiUser as User);
            }
          } catch (apiError) {
            console.error('API validation failed, using stored user data:', apiError);
            // If API call fails but we have stored user, continue using stored user
            // If no stored user, set user to null
            if (!storedUser) {
              setUser(null);
            }
          }
        } else {
          // No valid token, clear user
          setUser(null);
          
          // Clear any invalid tokens
          if (token && AuthService.isTokenExpired()) {
            AuthService.logout();
          }
        }
      } catch (err) {
        console.error('Authentication check failed:', err);
        setUser(null);
        setError(err instanceof Error ? err : new Error('Authentication failed'));
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };
    
    checkAuth();
  }, [isInitialized]);
  
  // Login function
  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Use AuthService login method
      const { user: authUser } = await AuthService.login(email, password);
      
      if (authUser) {
        setUser(authUser as User);
        return { success: true };
      } else {
        const errorMsg = 'Login failed: Invalid response';
        setError(new Error(errorMsg));
        return { success: false, error: errorMsg };
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Login failed: Unknown error';
      
      setError(new Error(errorMessage));
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Logout function
  const logout = useCallback(async (): Promise<void> => {
    try {
      // Use AuthService logout method to clear tokens/storage
      AuthService.logout();

      // Clear all cached tRPC/React Query data to prevent stale data from showing
      queryClient.clear();

      // Clear user data
      setUser(null);

      // Redirect to login page with a hard navigation to reset all in-memory state
      if (typeof window !== 'undefined') {
        window.location.href = '/auth/login';
      }
    } catch (err) {
      console.error('Logout error:', err);
    }
  }, []);
  
  // Get authentication token
  const getToken = useCallback(async (): Promise<string | null> => {
    // Use the AuthService to get the token consistently
    return AuthService.getToken();
  }, []);
  
  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        error,
        login,
        logout,
        getToken,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => useContext(AuthContext);

export { AuthContext };
export default AuthProvider;

/**
 * Higher order component to protect routes that require authentication
 */
export function withAuth<T>(Component: React.ComponentType<T>) {
  return function WithAuth(props: T) {
    const { isAuthenticated, isLoading } = useAuth();

    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        // Clear any invalid tokens and cached data
        AuthService.logout();
        queryClient.clear();

        // Use window.location for a hard redirect to ensure all state is reset
        if (typeof window !== 'undefined') {
          window.location.href = '/auth/login';
        }
      }
    }, [isLoading, isAuthenticated]);

    if (isLoading) {
      return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
      return null;
    }

    return <Component {...props} />;
  };
} 